<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <form class="form-container pb-md-5">
    <div v-for="field in fields" :key="field.id" class="form-item">
      <!-- <span>Label: </span>{{ field.label  }} -->
      <div
        v-if="field.type === 'select'"
        :disabled="field.id === 'municipio' ? false : field.disable"
        class="input-wrapper"
      >
        <label
          :for="field.id"
          class="input-label p-small"
          :style="'padding-bottom: 0.5rem'"
        >
          {{ field.label }} *
        </label>
        <div class="form-control-icon">
          <Icon v-if="!!field.icon" :name="field.icon" class="icon" />
          <v-select
            :id="field.id"
            v-model="model[field.id]"
            label="nome"
            style="margin: 0"
            :filter="
              (options: any, search: any) =>
                filterWithoutAccents(options, search)
            "
            :class="['form-select', getFieldError(field.id) ? 'invalid' : '']"
            :options="getOptions(field)"
            :placeholder="field.placeholder"
            :reduce="(el: any) => el.value || el.nome"
          >
            <template #no-options>Nenhuma opção encontrada</template>
          </v-select>
        </div>
        <div v-if="!!getFieldError(field.id)" class="invalid-feedback">
          <span class="text">{{ getFieldError(field.id) }}</span>
        </div>
      </div>
      <Input
        v-else
        :id="field.id"
        v-model="model[field.id]"
        :name="field.id"
        :placeholder="field.placeholder"
        :label="field.label"
        :icon="field.icon || ''"
        :icon-right="['cei', 'pis'].includes(field.id) ? 'circle-question' : ''"
        :icon-right-action="() => fieldHelp(field.id)"
        :type="field.type"
        :mask="field.mask === null ? undefined : field.mask"
        :is-disabled="field.disable"
        :error-log="getFieldError(field.id)"
        :min-length="field.min_length?.toString()"
        :is-required="field.required"
      />
    </div>
  </form>
  <TipsModal
    :show="tipModal.length > 0"
    :title="tipModal"
    :content-list="tipModalContent"
    :on-close="() => (tipModal = '')"
  />
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue'
  import { ObjMunicipio } from '@/store/Pinia/useMain.store'
  import { mapActions, mapState } from 'pinia'
  import {
    useDynamicForms,
    FormDataType,
  } from '@/store/Pinia/useDynamicForms.store'
  import { FieldType } from '@/infra/sisAr/dynamicForms.controller'
  import allValidates from '@/utils/validates'
  import Input from '../Atoms/Input.vue'
  import Icon from '../Atoms/Icon.vue'
  import { filterWithoutAccents } from '@/utils/search'

  import TipsModal from './TipsModal.vue'

  const TIP_MODAL_CONTENT_LIST = {
    CEI: [
      'O CEI é o Cadastro Específico do INSS. Ele é utilizado por pessoas físicas que prestam serviços de forma autônoma, sendo equiparadas às empresas e com um "status" semelhante. Porém, alguns destes profissionais hoje devem migrar para outras formas de cadastros.',
      'Um de seus objetivos é permitir que a pessoa física prestadora de serviços possa apurar e recolher corretamente o INSS sobre o valor recebido.',
      'O CEI é importante para o vínculo do empregado com o empregador. Para realizar as movimentações necessárias, ambos precisam constar no certificado.',
    ],
    PIS: [
      'O PIS é um programa de contribuição social que é pago pelas empresas e tem como objetivo financiar o pagamento do seguro-desemprego, abono salarial e outros benefícios trabalhistas.',
      'O número do PIS é gerado no primeiro emprego do trabalhador e pode ser encontrado na Carteira de Trabalho, nos extratos de seu Fundo de Garantia por Tempo de Serviço (FGTS), no Cartão Cidadão ou ser obtido em qualquer agência da Caixa.',
      'O PIS é importante para o vínculo do empregado com o empregador. Para realizar as movimentações necessárias, ambos precisam constar no certificado.',
    ],
  }

  export default defineComponent({
    name: 'DynamicFormsComponent',
    components: {
      Input,
      Icon,
      TipsModal,
    },
    props: {
      fields: {
        type: Array as PropType<FieldType[]>,
        required: true,
      },
      // Erros injetados por um componente pai (ex.: validações assíncronas)
      externalErrors: {
        type: Object as PropType<Record<string, string>>,
        default: () => ({}),
      },
    },
    emits: ['isValid', 'field-change'],
    data: () => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      model: {} as any,
      form: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fields: [{}] as any,
        municipios: [] as ObjMunicipio[],
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      errors: {} as any,
      tipModal: '',
    }),
    computed: {
      ...mapState(useDynamicForms, [
        'getMunicipiosByState',
        'getFormData',
        'getFormDataDefault',
      ]),
      tipModalContent() {
        return TIP_MODAL_CONTENT_LIST[this.tipModal] || []
      },
    },
    beforeUpdate() {
      const hasErros = Object.entries(this.errors).some(([, value]) => value)
      const requiredFields = this.form.fields
        .filter((field: { required: boolean }) => field.required)
        .map((field: { id: string }) => field.id)
      const emptyFields = Object.keys(this.model).filter(
        (key: string) => !this.model[key],
      )
      const hasEmptyRequired = emptyFields.some((field: string) =>
        requiredFields.includes(field),
      )

      this.$emit('isValid', !hasErros && !hasEmptyRequired)
    },
    methods: {
      ...mapActions(useDynamicForms, ['setUf']),
      buildModelFromFields() {
        return this.fields.reduce(
          (acc: Record<string, string>, curr: FieldType) => {
            acc[curr.id] = ''

            if (curr.name && curr.name !== curr.id) {
              acc[curr.name] = ''
            }

            return acc
          },
          {} as Record<string, string>,
        )
      },
      getLegacyNameById(fieldId: string) {
        const field = this.fields.find((x: FieldType) => x.id === fieldId)

        if (!field?.name || field.name === field.id) {
          return ''
        }

        return field.name
      },
      syncFieldNameAlias(fieldId: string, value: string) {
        const legacyName = this.getLegacyNameById(fieldId)

        if (!legacyName) {
          return
        }

        if (this.model[legacyName] !== value) {
          this.model[legacyName] = value
        }
      },
      syncModelAliasesFromSource(sourceObject: FormDataType) {
        this.fields.forEach((field: FieldType) => {
          if (!field.name || field.name === field.id) {
            return
          }

          const canonicalValue = sourceObject[field.id as keyof FormDataType]
          const legacyValue = sourceObject[field.name as keyof FormDataType]
          const valueToUse =
            canonicalValue !== undefined &&
            canonicalValue !== null &&
            canonicalValue !== ''
              ? canonicalValue
              : legacyValue

          if (
            valueToUse !== undefined &&
            valueToUse !== null &&
            valueToUse !== ''
          ) {
            this.model[field.id] = valueToUse
            this.model[field.name] = valueToUse
          }
        })
      },
      getOptions(field: FieldType) {
        if (field.id === 'municipio') {
          return this.form.municipios
        }
        const options = Object.entries(field.values).map(([key, value]) => ({
          nome: value,
          value: key,
        }))
        return options
      },

      fillWithMatchingAttributes(sourceObject: FormDataType) {
        if (
          !sourceObject ||
          typeof sourceObject !== 'object' ||
          Array.isArray(sourceObject)
        ) {
          throw new Error('The first argument must be an object.')
        }

        if (
          !this.model ||
          typeof this.model !== 'object' ||
          Array.isArray(this.model)
        ) {
          throw new Error('The second argument must be an object.')
        }

        Object.entries(sourceObject).forEach(([key, value]) => {
          if (Object.prototype.hasOwnProperty.call(this.model, key)) {
            this.model[key] = value
          }
        })

        this.syncModelAliasesFromSource(sourceObject)
      },
      validateField(
        fieldName: string,
        minLength: number,
        maxLength: number,
        currentValue?: string,
        oldValue?: string,
        isRequired?: boolean,
        isNumeric?: boolean,
      ) {
        // if (currentValue && currentValue.length >= minLength && currentValue.length <= maxLength) {
        //   this.errors[fieldName] = '';
        //   return;
        // }

        if (minLength && currentValue) {
          const realValue = isNumeric
            ? currentValue.replace(/\D/g, '')
            : currentValue
          if (realValue.length < minLength) {
            this.errors[fieldName] =
              `Este campo deve ter no mínimo ${minLength} caracteres`
            return
          }
        }

        if (maxLength && currentValue) {
          const realValue = isNumeric
            ? currentValue.replace(/\D/g, '')
            : currentValue
          if (realValue.length > maxLength) {
            this.errors[fieldName] =
              `Este campo deve ter no máximo ${maxLength} caracteres`
            return
          }
        }

        if (currentValue && currentValue.length > 0) {
          this.errors[fieldName] = ''
          return
        }

        this.errors[fieldName] = 'Este campo é obrigatório'

        if (currentValue === '' && !oldValue) {
          this.errors[fieldName] = ''
        }

        if (!isRequired) {
          this.errors[fieldName] = ''
        }
      },
      validateFieldWithValidation(
        fieldName: string,
        message: string,
        handler: (value: string) => boolean,
        currentValue?: string,
        oldValue?: string,
        isRequired?: boolean,
      ) {
        if (currentValue && currentValue.length > 0) {
          if (handler(currentValue)) {
            this.errors[fieldName] = ''
            return
          }
          this.errors[fieldName] = message
          return
        }

        this.errors[fieldName] = 'Este campo é obrigatório'

        if (currentValue === '' && !oldValue) {
          this.errors[fieldName] = ''
        }

        if (!isRequired) {
          this.errors[fieldName] = ''
        }
      },
      setWatchs() {
        const modelKeys = this.fields.map((field: FieldType) => field.id)

        modelKeys.forEach((key) => {
          const field = this.fields.find((x: FieldType) => key === x.id)
          const validationMethodName = field?.validationMethod

          if (key === 'municipio') {
            return
          }

          if (key === 'uf') {
            this.$watch(`model.${key}`, (curr: string, old: string) => {
              this.syncFieldNameAlias(key, curr)

              if (curr && curr.length > 0) {
                this.setUf(curr)
                this.form.municipios = this.getMunicipiosByState
                this.errors.uf = ''
                return
              }

              this.errors.uf = 'Este campo é obrigatório'

              if (curr === '' && !old) {
                this.errors.uf = ''
              }
            })
            return
          }

          if (validationMethodName && validationMethodName !== null) {
            this.$watch(
              `model.${key}`,
              (curr: string, old: string) => {
                this.syncFieldNameAlias(key, curr)

                this.validateFieldWithValidation(
                  key,
                  `Digite um ${key} válido`,
                  allValidates[validationMethodName],
                  curr,
                  old,
                  field?.required,
                )

                this.$emit('field-change', key, curr)
              },
              // eslint-disable-next-line function-paren-newline
            )
            return
          }
          this.$watch(`model.${key}`, (curr: string, old: string) => {
            this.syncFieldNameAlias(key, curr)

            this.validateField(
              key,
              field?.min_length as number,
              field?.max_length as number,
              curr,
              old,
              field?.required,
              field?.isNumeric,
            )

            this.$emit('field-change', key, curr)
          })
        })
      },
      filterWithoutAccents: filterWithoutAccents,
      fieldHelp(fieldId: string) {
        this.tipModal = fieldId.toUpperCase()
      },
      getFieldError(fieldId: string) {
        return this.errors[fieldId] || this.externalErrors[fieldId] || ''
      },
    },
    created() {
      this.form.fields = this.fields
      setTimeout(() => {
        const newModel = this.buildModelFromFields()
        const errorsModel = this.fields.reduce(
          (acc: Record<string, string>, curr: FieldType) => {
            acc[curr.id] = ''
            return acc
          },
          {} as Record<string, string>,
        )

        this.errors = { ...errorsModel }
        this.model = { ...newModel }
        const formData = { ...this.getFormData }
        this.fillWithMatchingAttributes(formData)
        this.setWatchs()
      }, 100)
    },
  })
</script>
<style lang="stylus" scoped>
  .input-label
    text-align: left
    width: 100%
  .icon
    width: 1.25rem
    height: 1.5rem
    margin-top: 18px !important
    color: var(--color-primary)

  .form-view
    text-align: left
    overflow: auto

  .form-container
    display: flex
    flex-wrap: wrap
    justify-content: space-between
    height: max-content
  .form-item
    flex: 0 0 49%
    margin-top: 2rem
  .wrapper-info
    border: 1px solid #000
    border-radius: 12px
    padding: 10px
    text-align: left

  @media screen and (max-width: 900px)
    .form-container
      flex-direction: column
    .form-item
      flex: 1
</style>
